import React, { useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Check, ChevronDown } from "lucide-react";
import { services } from "../data/mockData";
import Icon from "./Icon";
import { openWhatsApp } from "../lib/whatsapp";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "./ui/carousel";

const ServicesSection = () => {
  return (
    <section id="servicios" className="py-20 bg-background text-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex text-center justify-center items-center gap-4 flex-col mb-16">
          <Badge>Servicios</Badge>
          <div className="flex gap-2 flex-col">
            <h2 className="text-3xl md:text-5xl tracking-tighter max-w-xl text-center font-regular">
              Servicios especializados para tus equipos
            </h2>
            <p className="text-lg leading-relaxed tracking-tight text-muted-foreground max-w-2xl text-center">
              Soluciones completas para todos tus equipos.
            </p>
          </div>
        </div>
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:items-start">
          {services.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>
        <div className="md:hidden">
          <Carousel opts={{ align: "start", loop: true }} className="w-full">
            <CarouselContent className="-ml-4 items-start">
              {services.map((service) => (
                <CarouselItem key={service.id} className="pl-4 basis-4/5 flex flex-col">
                  <div className="p-1 flex-grow h-full">
                    <ServiceCard service={service} />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-20 lg:-left-24" />
            <CarouselNext className="hidden md:flex -right-20 lg:-right-24" />
          </Carousel>
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ service }) => {
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const toggleFeatures = () => setFeaturesOpen((prev) => !prev);
  const cardClass = `card flex flex-col transition-all duration-300 md:hover:-translate-y-1 transform md:hover:scale-105 ${
    featuresOpen ? "h-full self-stretch" : ""
  }`;

  return (
    <Card
      className={`w-full rounded-2xl overflow-visible card-flat ${cardClass}`}
      role="button"
      tabIndex={0}
      onClick={toggleFeatures}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggleFeatures();
        }
      }}
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl font-semibold text-foreground">
          <Icon name={service.icon} className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
          {service.title}
        </CardTitle>
        <CardDescription className="text-muted-foreground leading-relaxed pt-2">
          {service.description}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-grow flex flex-col gap-6">
        {service.price && (
          <p className="flex items-center gap-2 text-xl">
            <span className="text-3xl font-semibold">{service.price}</span>
            <span className="text-sm text-muted-foreground">precio estimado</span>
          </p>
        )}
        {featuresOpen && (
          <div className="flex flex-col gap-4">
            {service.features.map((feature) => (
              <div key={feature} className="flex flex-row gap-4">
                <Check className="w-4 h-4 mt-1 text-primary" />
                <div className="flex flex-col">
                  <p>{feature}</p>
                  <p className="text-muted-foreground text-sm">Detalle incluido en el servicio.</p>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-auto flex flex-col gap-3">
          <Button
            variant="link"
            onClick={(event) => {
              event.stopPropagation();
              toggleFeatures();
            }}
            className="text-sm font-semibold text-primary flex items-center gap-2 p-0 h-auto"
          >
          {featuresOpen ? "Ver menos" : "Ver más"}
            <ChevronDown className={`h-4 w-4 transition-transform ${featuresOpen ? "rotate-180" : ""}`} />
          </Button>
          <Button
            variant="outline"
            className="w-full dark:border-border dark:text-foreground"
            onClick={(event) => {
              event.stopPropagation();
              toggleFeatures();
              openWhatsApp(`Hola, me gustaría saber más sobre el servicio de ${service.title}.`);
            }}
          >
            Más información
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ServicesSection;
